import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { Chip } from '../components/ui.jsx';
import { ghs } from '../lib/money.js';

const today = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

const DESCRIPTION = {
  spa: 'Services, appointments, commission',
  fashion: 'Retail, variants, stock',
  bites: 'Food, menu, kitchen dockets',
  apartment: 'Stays, folio',
  hotel: 'Rooms, folio, housekeeping',
  laundry: 'Wash and iron, by the piece or the kilo'
};

/** The console is the whole place, not a seventh business, so it never enters the grid. */
const CONSOLE = { id: 'group', slug: 'group', short_name: 'Console', colour: '#0F172A', module: 'group' };

export function Boarding({ user, businesses, onEnter, onSignOut }) {
  const [refused, setRefused] = useState(null);
  const [caps, setCaps] = useState(null);
  const [glance, setGlance] = useState(null);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    api.capabilities().then(setCaps).catch(() => setCaps({ capabilities: {} }));
  }, []);

  // Only fetched for the rungs that have the roll-up, so a cashier's till does no work it
  // will never show.
  useEffect(() => {
    if (!caps?.capabilities?.dashboard) return;
    api.dashboard().then(setGlance).catch(() => {});
    api.unreadCount().then(d => setUnread(d.unread)).catch(() => {});
  }, [caps]);

  async function enter(b) {
    if (b.external_url) return;              // Laundry still runs on its own system
    try {
      await api.enterBusiness(b.slug);
      onEnter(b);
    } catch (e) {
      // The server said no and wrote it down. Say so plainly rather than leaving somebody
      // to wonder whether it was a glitch: being told is fairer than being watched quietly.
      setRefused({
        title: e.data?.title || `${b.short_name} is not yours to open`,
        message: e.data?.message
          || 'You do not hold this business. This attempt has been recorded in the audit log.',
        attempts: e.data?.attempts || 1
      });
    }
  }

  const c = caps?.capabilities || {};

  return (
    <div style={{ minHeight: '100%', padding: '48px 64px 64px', maxWidth: 1500, margin: '0 auto' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', marginBottom: 28 }}>
        <div style={{ flex: 1 }}>
          <div className="t-heading-xl">Ezel Place</div>
          <div className="t-body-m secondary">{today}</div>
        </div>
        <span className="t-label-l secondary">{user.name} · {roleLabel(caps, user.role)}</span>
        <button className="btn btn-ghost btn-m" onClick={onSignOut}>Sign out</button>
      </header>

      {refused && <Refused refused={refused} onClose={() => setRefused(null)} />}

      {c.dashboard && (
        <Console glance={glance} unread={unread} caps={c}
          onOpen={screen => onEnter({ ...CONSOLE, openAt: screen })} />
      )}

      <div className="t-label-xs muted" style={{ margin: '4px 0 14px' }}>
        {c.dashboard ? 'OR GO STRAIGHT INTO ONE BUSINESS' : 'YOUR BUSINESSES'}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-xl)' }}>
        {businesses.map(b => <Tile key={b.id} business={b} onClick={() => enter(b)} />)}
      </div>
    </div>
  );
}

/**
 * Everything above a single business, in one strip: today's takings across the whole place,
 * anything that needs somebody, and one click into each console screen. This is the first
 * thing the super admin and the admin see, because "enter Ezel Fashion" is almost never
 * what either of them actually wants on landing.
 */
function Console({ glance, unread, caps, onOpen }) {
  const t = glance?.totals;
  const delta = t && t.yesterday
    ? Math.round(((t.today - t.yesterday) / t.yesterday) * 100)
    : null;

  const entries = [
    ['Dashboard', 'All six businesses, today, this week, this month', true],
    ['Users and roles', 'Onboard admins, managers and cashiers', caps.onboard],
    ['Report inbox', 'What the managers have sent up', caps.inbox, unread],
    ['Audit log', 'Every action, who did it and when', caps.auditLog],
    ['Businesses and tills', 'The shape of the system itself', caps.manageBusinesses]
  ].filter(e => e[2]);

  return (
    <section style={{
      border: '1px solid var(--color-border-default)', borderRadius: 'var(--radius-lg)',
      background: 'var(--color-bg-surface)', overflow: 'hidden', marginBottom: 34
    }}>
      <div style={{
        display: 'flex', gap: 28, alignItems: 'flex-end', flexWrap: 'wrap',
        padding: '20px 24px', background: 'var(--color-bg-subtle)',
        borderBottom: '1px solid var(--color-border-default)'
      }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div className="t-label-xs muted">ACROSS ALL SIX, TODAY</div>
          <div className="money t-money-xl" style={{ marginTop: 2 }}>
            {t ? ghs(t.today) : 'GHS ...'}
          </div>
          <div className="t-body-s muted">
            {t ? `${t.salesToday} sale${t.salesToday === 1 ? '' : 's'}` : 'loading'}
            {delta !== null && (
              <span style={{ color: delta >= 0 ? 'var(--color-status-success)' : 'var(--color-status-danger)' }}>
                {'  '}{delta >= 0 ? '+' : ''}{delta}% on yesterday
              </span>
            )}
          </div>
        </div>
        <Stat label="THIS WEEK" value={t && ghs(t.week)} />
        <Stat label="THIS MONTH" value={t && ghs(t.month)} />
        <Stat label="LAST MONTH" value={t && ghs(t.lastMonth)} quiet />
      </div>

      {glance?.attention?.length > 0 && (
        <div style={{ padding: '12px 24px', display: 'flex', gap: 10, flexWrap: 'wrap', borderBottom: '1px solid var(--color-border-default)' }}>
          {glance.attention.map((a, i) => <Chip key={i} tone={a.tone}>{a.text}</Chip>)}
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap' }}>
        {entries.map(([label, blurb, , badge], i) => (
          <button key={label} onClick={() => onOpen(label)}
            style={{
              flex: '1 1 220px', minWidth: 200, textAlign: 'left', cursor: 'pointer',
              padding: '16px 24px', background: 'transparent', font: 'inherit', color: 'inherit',
              border: 0, borderLeft: i === 0 ? 0 : '1px solid var(--color-border-default)'
            }}
            onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-bg-hover)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="t-heading-s">{label}</span>
              {badge > 0 && <Chip tone="danger">{badge}</Chip>}
            </span>
            <span className="t-body-s muted" style={{ display: 'block', marginTop: 2 }}>{blurb}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

/**
 * Shown when somebody opens a business they do not hold.
 *
 * Deliberately a stop, not a line of red text they can scroll past. Two things have to land:
 * that it did not work, and that it was written down with their name on it. Being told is
 * fairer than being watched quietly, and it is also the only version that deters anything.
 */
function Refused({ refused, onClose }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 70, display: 'grid', placeItems: 'center',
      padding: 24, background: 'rgba(2,6,23,0.5)'
    }}>
      <div className="card" style={{ width: 520, boxShadow: 'var(--elevation-lg)', overflow: 'hidden' }}>
        <div style={{ height: 4, background: 'var(--color-status-danger)' }} />
        <div style={{ padding: 24, display: 'grid', gap: 12 }}>
          <div className="t-heading-l">{refused.title}</div>
          <div className="t-body-m secondary">{refused.message}</div>

          {refused.attempts > 1 && (
            <Chip tone="danger">
              This is attempt {refused.attempts} in the last 30 days, and each one is on the record
            </Chip>
          )}

          <div className="t-body-s muted">
            If you should have access to it, an admin can grant it from Users and roles.
          </div>
        </div>
        <div style={{ padding: 16, borderTop: '1px solid var(--color-border-default)', display: 'flex' }}>
          <span style={{ flex: 1 }} />
          <button className="btn btn-primary btn-m" onClick={onClose}>I understand</button>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ label, value, quiet }) => (
  <div style={{ minWidth: 130 }}>
    <div className="t-label-xs muted">{label}</div>
    <div className={quiet ? 'money t-money-m muted' : 'money t-money-m'} style={{ marginTop: 2 }}>
      {value || 'GHS ...'}
    </div>
  </div>
);

function Tile({ business, onClick }) {
  const external = Boolean(business.external_url);
  return (
    <button
      onClick={onClick}
      disabled={external}
      className="card"
      style={{
        width: 336, height: 168, padding: 0, textAlign: 'left', cursor: external ? 'default' : 'pointer',
        overflow: 'hidden', display: 'flex', flexDirection: 'column', background: 'var(--color-bg-surface)',
        font: 'inherit', color: 'inherit'
      }}>
      {/* The accent band is how an operator knows which till they are standing at */}
      <span style={{ height: 8, background: business.colour, display: 'block', width: '100%' }} />
      <span style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
        <span className="t-heading-l">{business.short_name}</span>
        <span className="t-body-s muted">{DESCRIPTION[business.slug] || ''}</span>
        <span style={{ flex: 1 }} />
        {external
          ? <Chip tone="warning">Separate system</Chip>
          : <span className="t-label-xs muted">OPEN</span>}
      </span>
    </button>
  );
}

/** The rung's own label, so the top rung reads as Super admin rather than as the owner. */
function roleLabel(caps, role) {
  const known = { owner: 'Super admin', admin: 'Admin', manager: 'Manager', cashier: 'Cashier' };
  return known[role] || role;
}
